import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

/**
 * Analytics Component
 * Comprehensive analytics dashboard for admin users
 */
const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  const [error, setError] = useState('');

  // Mock analytics data for now
  const analytics = {
    user_growth: { growth_rate: '+12.5%' },
    project_stats: { active_projects: 15 },
    system_performance: { 
      uptime_percentage: 99.8,
      avg_response_time: 145,
      error_rate: 0.2,
      total_requests: 25847
    },
    user_activity: {
      daily_active_users: 28,
      weekly_active_users: 156,
      monthly_active_users: 423,
      average_session_duration: '14m 32s'
    },
    popular_features: [
      { feature: 'User Management', percentage: 85, usage_count: 340 },
      { feature: 'Project Dashboard', percentage: 72, usage_count: 288 },
      { feature: 'Analytics', percentage: 58, usage_count: 232 },
      { feature: 'Settings', percentage: 45, usage_count: 180 }
    ],
    recent_activities: [
      { id: 1, user: 'John Doe', action: 'Login', timestamp: '2024-10-16T10:30:00Z' },
      { id: 2, user: 'Jane Smith', action: 'Created', timestamp: '2024-10-16T09:15:00Z' },
      { id: 3, user: 'Admin User', action: 'Updated', timestamp: '2024-10-16T08:45:00Z' },
      { id: 4, user: 'Bob Wilson', action: 'Login', timestamp: '2024-10-16T08:30:00Z' }
    ]
  };

  const getActivityColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'login': return 'success';
      case 'logout': return 'default';
      case 'created': return 'primary';
      case 'updated': return 'info';
      case 'deleted': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          <AnalyticsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Analytics Dashboard
        </Typography>
        
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="quarter">Last Quarter</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Key Performance Indicators */}
        <Box>
          <Typography variant="h6" gutterBottom>Key Performance Indicators</Typography>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        User Growth
                      </Typography>
                      <Typography variant="h5">
                        {analytics.user_growth.growth_rate}
                      </Typography>
                    </Box>
                    <TrendingUpIcon color="primary" />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Active Projects
                      </Typography>
                      <Typography variant="h5">
                        {analytics.project_stats.active_projects}
                      </Typography>
                    </Box>
                    <AssignmentIcon color="success" />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        System Uptime
                      </Typography>
                      <Typography variant="h5">
                        {analytics.system_performance.uptime_percentage}%
                      </Typography>
                    </Box>
                    <TimelineIcon color="info" />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Daily Active Users
                      </Typography>
                      <Typography variant="h5">
                        {analytics.user_activity.daily_active_users}
                      </Typography>
                    </Box>
                    <PeopleIcon color="warning" />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* User Activity and System Performance */}
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardHeader title="User Activity" />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Daily Active Users
                    </Typography>
                    <Typography variant="h6">
                      {analytics.user_activity.daily_active_users}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Weekly Active Users
                    </Typography>
                    <Typography variant="h6">
                      {analytics.user_activity.weekly_active_users}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Monthly Active Users
                    </Typography>
                    <Typography variant="h6">
                      {analytics.user_activity.monthly_active_users}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Avg. Session Duration
                    </Typography>
                    <Typography variant="h6">
                      {analytics.user_activity.average_session_duration}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card>
              <CardHeader title="System Performance" />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Avg. Response Time
                    </Typography>
                    <Typography variant="h6">
                      {analytics.system_performance.avg_response_time}ms
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Uptime
                    </Typography>
                    <Typography variant="h6">
                      {analytics.system_performance.uptime_percentage}%
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Error Rate
                    </Typography>
                    <Typography variant="h6">
                      {analytics.system_performance.error_rate}%
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Total Requests
                    </Typography>
                    <Typography variant="h6">
                      {analytics.system_performance.total_requests.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Popular Features and Recent Activities */}
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardHeader title="Popular Features" />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={2}>
                  {analytics.popular_features.map((feature, index) => (
                    <Box key={index}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2">{feature.feature}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.percentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={feature.percentage}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card>
              <CardHeader title="Recent Activities" />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.recent_activities.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell>{activity.user}</TableCell>
                          <TableCell>
                            <Chip
                              label={activity.action}
                              color={getActivityColor(activity.action) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(activity.timestamp).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Export Section */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Export Analytics Data</Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Data Type</InputLabel>
              <Select label="Data Type" defaultValue="users">
                <MenuItem value="users">Users</MenuItem>
                <MenuItem value="projects">Projects</MenuItem>
                <MenuItem value="activity">Activity</MenuItem>
                <MenuItem value="performance">Performance</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Format</InputLabel>
              <Select label="Format" defaultValue="csv">
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              Export
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Analytics;